/* Suchen, Auswählen und Ersetzen über die ganze Map: reine Funktionen auf dem Dokument. */
import type { Doc, Entity, Vec3 } from './types.ts';
import Core from '../core.ts';
/** Fläche: Brush-ID und Index der Fläche */
export interface FaceRef { b: string; f: number }
export interface EntQuery { cls?: string; key?: string; val?: string }

const Find = (function (Core) {
  'use strict';
  const esc = (t: string): string => t.replace(/[.+^${}()|[\]\\]/g, '\\$&');
  /** Muster mit * und ? (ohne Platzhalter: Teilstring, ohne Groß-/Kleinschreibung). Leeres Muster passt auf alles. */
  function matcher(pattern?: unknown): (t: unknown) => boolean {
    const pat = String(pattern === undefined || pattern === null ? '' : pattern).trim();
    if (!pat) return () => true;
    if (/[*?]/.test(pat)) { const re = new RegExp('^' + esc(pat).replace(/\*/g, '.*').replace(/\?/g, '.') + '$', 'i'); return (t: unknown) => re.test(String(t)); }
    const lo = pat.toLowerCase();
    return (t: unknown) => String(t).toLowerCase().indexOf(lo) >= 0;
  }
  const isPoint = (e: Entity, i: number): boolean => i > 0 && !e.brushes.length;

  /** Flächen mit passender Textur: [{b: brushId, f: faceIndex}] */
  function facesByTex(doc: Doc, pat: unknown, scopeB?: Set<string> | null): FaceRef[] {
    const m = matcher(pat), out: FaceRef[] = [];
    doc.ents.forEach((e) => e.brushes.forEach((b) => { if (scopeB && !scopeB.has(b.id)) return; b.faces.forEach((f, i) => { if (m(f.tex)) out.push({ b: b.id, f: i }); }); }));
    return out;
  }
  function brushesByTex(doc: Doc, pat: unknown, scopeB?: Set<string> | null): Set<string> {
    const ids = new Set<string>(); facesByTex(doc, pat, scopeB).forEach((x) => ids.add(x.b)); return ids;
  }
  /** Entities nach Klasse, Feld und Wert. Ergebnis {b: Brush-IDs der Brush-Entities, e: IDs der Punkt-Entities, ents: [Entity-Objekte]} */
  function entsBy(doc: Doc, o: EntQuery): { b: Set<string>; e: Set<string>; ents: Entity[] } {
    const mc = matcher(o.cls), mv = matcher(o.val), key = (o.key || '').trim(), out = { b: new Set<string>(), e: new Set<string>(), ents: [] as Entity[] };
    doc.ents.forEach((e, i) => {
      if (i === 0) return;
      if (!mc(e.kv.classname || '')) return;
      if (key) { if (!(key in e.kv)) return; if (!mv(e.kv[key])) return; } else if (o.val && !Object.keys(e.kv).some((k) => mv(e.kv[k]))) return;
      out.ents.push(e);
      if (isPoint(e, i)) out.e.add(e.id); else e.brushes.forEach((b) => out.b.add(b.id));
    });
    return out;
  }
  /** Textur ersetzen: from (Muster) -> to. scopeB: nur diese Brushes (Set) oder null = alle. Ergebnis {doc, faces, brushes} */
  function replaceTex(doc: Doc, from: unknown, toTex: unknown, scopeB?: Set<string> | null): { doc: Doc; faces: number; brushes: number } {
    const to = String(toTex || '').trim(); if (!to) return { doc: doc, faces: 0, brushes: 0 };
    const m = matcher(from); let faces = 0, brushes = 0;
    const all = new Set<string>(); doc.ents.forEach((e) => e.brushes.forEach((b) => all.add(b.id)));
    const d = Core.mapBrushes(doc, scopeB || all, (b) => {
      let hit = 0; const fs = b.faces.map((f) => { if (m(f.tex) && f.tex.toUpperCase() !== to.toUpperCase()) { hit++; return Object.assign({}, f, { tex: to }); } return f; });
      if (!hit) return b; faces += hit; brushes++;
      return Object.assign({}, b, { srcText: undefined, faces: fs });
    });
    return { doc: faces ? d : doc, faces: faces, brushes: brushes };
  }
  /** Feldwert ersetzen in den Entities, die zu cls/key passen: Wert from (Muster; leer = jeder) -> to. Mit rename: Feld umbenennen.
   *  Ergebnis {doc, n} */
  function replaceKV(doc: Doc, o: EntQuery, to: string | null): { doc: Doc; n: number } {
    const key = (o.key || '').trim(); if (!key) return { doc: doc, n: 0 };
    const mc = matcher(o.cls), mv = matcher(o.val); let n = 0;
    const ents = doc.ents.map((e, i) => {
      if (i === 0 || !mc(e.kv.classname || '') || !(key in e.kv) || !mv(e.kv[key])) return e;
      n++;
      const kv = Object.assign({}, e.kv);
      if (to === '' || to === null) delete kv[key]; else kv[key] = to;
      return Object.assign({}, e, { kv: kv });
    });
    return { doc: n ? Object.assign({}, doc, { ents: ents }) : doc, n: n };
  }
  /** Flächen mit (fast) gleicher Normale wie ref {b,f}; scopeB: nur diese Brushes oder null */
  function facesByNormal(doc: Doc, ref: FaceRef, scopeB?: Set<string> | null, tol?: number): FaceRef[] {
    let rn: Vec3 | null = null; doc.ents.forEach((e) => e.brushes.forEach((b) => { if (b.id === ref.b && b.faces[ref.f]) rn = Core.faceNormal(b.verts, b.faces[ref.f].vi); }));
    if (!rn) return []; const rv = rn as Vec3, cs = Math.cos((tol === undefined ? 2 : tol) * Math.PI / 180), out: FaceRef[] = [];
    doc.ents.forEach((e) => e.brushes.forEach((b) => { if (scopeB && !scopeB.has(b.id)) return; b.faces.forEach((f, i) => { if (Core.dot(Core.faceNormal(b.verts, f.vi), rv) >= cs) out.push({ b: b.id, f: i }); }); }));
    return out;
  }
  /** Alle Flächen der Brushes */
  function facesOf(doc: Doc, ids: Set<string>): FaceRef[] { const out: FaceRef[] = []; doc.ents.forEach((e) => e.brushes.forEach((b) => { if (ids.has(b.id)) b.faces.forEach((f, i) => out.push({ b: b.id, f: i })); })); return out; }
  return { facesByNormal, facesOf, matcher, facesByTex, brushesByTex, entsBy, replaceTex, replaceKV };
})(Core);
export default Find;
